"use client";

import { useCallback, useEffect, useRef } from "react";
import { PLUGINS } from "@/config/constants";
import { fetchIntercomJwt } from "@/lib/auth/intercom-jwt";
import { trackActivity } from "@/lib/integrations/activity-events";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type IntercomCommand =
  | "boot"
  | "shutdown"
  | "update"
  | "show"
  | "hide"
  | "showNewMessage"
  | "trackEvent";

type IntercomPayload = Record<string, unknown> | string;
type IntercomFn = (
  command: IntercomCommand,
  payload?: IntercomPayload,
  metadata?: Record<string, unknown>,
) => void;

declare global {
  interface Window {
    Intercom?: IntercomFn;
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function intercom(
  command: IntercomCommand,
  payload?: IntercomPayload,
  metadata?: Record<string, unknown>,
) {
  if (typeof window === "undefined" || typeof window.Intercom !== "function") return;
  if (metadata !== undefined) {
    window.Intercom(command, payload, metadata);
  } else if (payload !== undefined) {
    window.Intercom(command, payload);
  } else {
    window.Intercom(command);
  }
}

const isActive = PLUGINS.intercom.isActive;

const TOKEN_MAX_AGE_MS = 50 * 60 * 1000;

// Shared by every useIntercom() instance: boot runs in one component, show in many.
// `generation` changes on every boot, shutdown and unmount so a late token fetch can tell its session ended.
const session = { generation: 0, signedIn: false, tokenFetchedAt: null as number | null, retriedAfterFailure: false };

function resetSession(signedIn: boolean, tokenFetchedAt: number | null) {
  session.generation++;
  session.signedIn = signedIn;
  session.tokenFetchedAt = tokenFetchedAt;
  session.retriedAfterFailure = false;
}

/** True when a signed-in session needs a new token: older than 50 minutes, or the first fetch failed and has not been retried. */
function needsFreshToken(): boolean {
  if (!session.signedIn) return false;
  if (session.tokenFetchedAt === null) return !session.retriedAfterFailure;
  return Date.now() - session.tokenFetchedAt > TOKEN_MAX_AGE_MS;
}

function supportResult(): "opened" | "unavailable" {
  return typeof window !== "undefined" && typeof window.Intercom === "function" ? "opened" : "unavailable";
}

/** Runs `open` after refreshing the signed token when it is stale; open always runs, even if the refresh fails. */
function openWithFreshToken(open: () => void) {
  if (!needsFreshToken()) {
    open();
    return;
  }
  if (session.tokenFetchedAt === null) session.retriedAfterFailure = true;
  const generation = session.generation;
  void fetchIntercomJwt().then((intercomJwt) => {
    // A token fetched for a previous session must never reach the current one.
    if (intercomJwt && generation === session.generation && session.signedIn) {
      session.tokenFetchedAt = Date.now();
      intercom("update", { intercom_user_jwt: intercomJwt });
    }
    open();
  });
}

/**
 * Signed boot payload: identity and attributes travel inside the token, so only
 * app_id and the launcher flag are sent unsigned.
 */
function buildSignedPayload(intercomJwt: string): Record<string, unknown> {
  return {
    ...buildAnonymousPayload(),
    intercom_user_jwt: intercomJwt,
  };
}

function buildAnonymousPayload(): Record<string, unknown> {
  return {
    app_id: PLUGINS.intercom.appId,
    // Keep the messenger hidden until the user clicks "Help & Support".
    // Must be set in the initial boot payload — relying on a follow-up
    // `update` call causes a flash of the default launcher on page load.
    hide_default_launcher: true,
  };
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Centralised Intercom lifecycle hook.
 *
 * - `boot()` — identify the signed-in user with a server-signed token (anonymous if the token call fails) and start the messenger (hidden by default)
 * - `shutdown()` — end the session (must be called on logout)
 * - `show()` / `hide()` — toggle the messenger
 * - `sendMessage(msg)` — open composer with pre-filled text
 */
export function useIntercom() {
  const bootedRef = useRef(false);
  // Bumped by every boot and shutdown so a token fetch that outlives a sign-out never boots the old user.
  const bootSeqRef = useRef(0);
  const onHideRegisteredRef = useRef(false);

  // Clean up on unmount (e.g. full app teardown)
  useEffect(() => {
    return () => {
      if (bootedRef.current) {
        intercom("shutdown");
        bootedRef.current = false;
        resetSession(false, null);
      }
    };
  }, []);

  const boot = useCallback(async () => {
    if (!isActive) return;

    const seq = ++bootSeqRef.current;
    const intercomJwt = await fetchIntercomJwt();
    if (seq !== bootSeqRef.current) return;

    // Intercom ignores a second boot, so end the previous session (e.g. the anonymous one after sign-out) first.
    if (bootedRef.current) intercom("shutdown");
    resetSession(true, intercomJwt ? Date.now() : null);
    onHideRegisteredRef.current = false;
    intercom("boot", intercomJwt ? buildSignedPayload(intercomJwt) : buildAnonymousPayload());
    bootedRef.current = true;
    intercom("update", { hide_default_launcher: true });

    if (!onHideRegisteredRef.current && typeof window !== "undefined" && typeof window.Intercom === "function") {
      onHideRegisteredRef.current = true;
      (window.Intercom as unknown as (event: string, cb: () => void) => void)(
        "onHide",
        () => intercom("update", { hide_default_launcher: true })
      );
    }
  }, []);

  const shutdown = useCallback(() => {
    if (!isActive) return;

    bootSeqRef.current++;
    intercom("shutdown");
    resetSession(false, null);
    onHideRegisteredRef.current = false;

    intercom("boot", buildAnonymousPayload());
    bootedRef.current = true;
  }, []);

  const update = useCallback((attrs: Record<string, unknown>) => {
    if (!isActive) return;
    intercom("update", attrs);
  }, []);

  const show = useCallback(() => {
    if (!isActive) return;
    trackActivity("support.open", { mode: "show", result: supportResult() });
    openWithFreshToken(() => {
      intercom("show");
      intercom("update", { hide_default_launcher: true });
    });
  }, []);

  const hide = useCallback(() => {
    intercom("hide");
  }, []);

  const sendMessage = useCallback((message: string) => {
    trackActivity("support.open", { mode: "new_message", result: supportResult() });
    openWithFreshToken(() => intercom("showNewMessage", message));
  }, []);

  const trackEvent = useCallback((name: string, metadata?: Record<string, unknown>) => {
    if (!isActive) return;
    intercom("trackEvent", name, metadata);
  }, []);

  return { boot, shutdown, update, show, hide, sendMessage, trackEvent, isActive };
}
